import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Mail,
  Phone,
  MapPin,
  Headset,
  Briefcase,
  Shield,
  FileText,
  CircleCheck,
  Send,
} from "lucide-react";
import MarketingLayout from "../marketing/MarketingLayout";
import SiteMeta from "../marketing/SiteMeta";
import ScrollReveal from "../marketing/ScrollReveal";
import ContactCard from "../marketing/ContactCard";
import { COMPANY, MARKETING_ROUTES } from "../marketing/routes";
import { CONTACT_SUBJECTS, submitContactEnquiry } from "../../Moduels/contact/contactApi";
import { useToast } from "../../Moduels/Context/ToastContext";
import "./aboutUs.css";
import "./contactUs.css";

const SUPPORT_TOPICS = [
  "Account and login assistance",
  "HRMS and payroll software support",
  "Employee and company setup",
  "Subscription and plan-related queries",
  "Billing and payment issues",
  "Subscription cancellation",
  "Refund requests",
  "Technical issues",
  "Product and feature-related questions",
];

const INITIAL_FORM = {
  name: "",
  company_name: "",
  email: "",
  phone: "",
  subject: "",
  message: "",
  website: "",
};

function ContactForm() {
  const toast = useToast();
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const validate = () => {
    const next = {};
    if (form.name.trim().length < 2) next.name = "Enter your name.";
    if (form.company_name.trim().length < 2) next.company_name = "Enter your company name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) next.email = "Enter a valid email address.";
    const digits = form.phone.replace(/\D/g, "").replace(/^91/, "");
    if (digits.length !== 10 || !/^[6-9]/.test(digits)) next.phone = "Enter a valid 10-digit Indian mobile number.";
    if (!form.subject) next.subject = "Select a subject.";
    if (form.message.trim().length < 10) next.message = "Please enter a message with at least 10 characters.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await submitContactEnquiry({
        name: form.name.trim(),
        company_name: form.company_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        subject: form.subject,
        message: form.message.trim(),
        website: form.website,
      });
      setSent(true);
      setForm(INITIAL_FORM);
      toast.success("Your enquiry has been sent.");
    } catch (err) {
      const message = err?.message || "Unable to send your enquiry. Please try again.";
      setErrors((prev) => ({ ...prev, form: message }));
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div className="cu-form-success" role="status">
        <CircleCheck size={28} />
        <h3>Thank you</h3>
        <p>Your enquiry has been received. We will make reasonable efforts to respond as promptly as possible.</p>
        <button type="button" className="mk-btn mk-btn-primary" onClick={() => setSent(false)}>
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form className="cu-form" onSubmit={onSubmit} noValidate>
      <label className="cu-hp" aria-hidden="true">
        Website
        <input
          tabIndex={-1}
          autoComplete="off"
          value={form.website}
          onChange={(e) => setField("website", e.target.value)}
        />
      </label>
      <div className="cu-form-grid">
        <label>
          Name
          <input
            type="text"
            name="name"
            autoComplete="name"
            placeholder="Enter your name"
            value={form.name}
            onChange={(e) => setField("name", e.target.value)}
          />
          {errors.name ? <span className="cu-err">{errors.name}</span> : null}
        </label>
        <label>
          Company Name
          <input
            type="text"
            name="company"
            autoComplete="organization"
            placeholder="Enter your company name"
            value={form.company_name}
            onChange={(e) => setField("company_name", e.target.value)}
          />
          {errors.company_name ? <span className="cu-err">{errors.company_name}</span> : null}
        </label>
        <label>
          Email Address
          <input
            type="email"
            name="email"
            autoComplete="email"
            placeholder="Enter your email address"
            value={form.email}
            onChange={(e) => setField("email", e.target.value)}
          />
          {errors.email ? <span className="cu-err">{errors.email}</span> : null}
        </label>
        <label>
          Phone Number
          <input
            type="tel"
            name="phone"
            autoComplete="tel"
            placeholder="Enter your phone number"
            value={form.phone}
            onChange={(e) => setField("phone", e.target.value)}
          />
          {errors.phone ? <span className="cu-err">{errors.phone}</span> : null}
        </label>
      </div>
      <label>
        Subject
        <select
          name="subject"
          value={form.subject}
          onChange={(e) => setField("subject", e.target.value)}
        >
          <option value="">Select or enter your subject</option>
          {CONTACT_SUBJECTS.map((subject) => (
            <option key={subject} value={subject}>{subject}</option>
          ))}
        </select>
        {errors.subject ? <span className="cu-err">{errors.subject}</span> : null}
      </label>
      <label>
        Message
        <textarea
          name="message"
          rows={5}
          placeholder="Enter your message"
          value={form.message}
          onChange={(e) => setField("message", e.target.value)}
        />
        {errors.message ? <span className="cu-err">{errors.message}</span> : null}
      </label>
      {errors.form ? <p className="cu-err cu-err-form">{errors.form}</p> : null}
      <button type="submit" className="mk-btn mk-btn-primary cu-submit" disabled={submitting}>
        {submitting ? "Sending…" : <>Submit <Send size={16} /></>}
      </button>
      <p className="cu-form-note">
        By submitting the contact form, you agree that Brixigo Technologies Private Limited may use the information provided to respond to your enquiry in accordance with our{" "}
        <Link to={MARKETING_ROUTES.privacy}>Privacy Policy</Link>.
      </p>
    </form>
  );
}

export default function ContactUsPage() {
  return (
    <MarketingLayout className="contact-us-page">
      <SiteMeta
        title="Contact Us | Brixigo HRMS & Payroll"
        description="Contact Brixigo Technologies Private Limited for HRMS, payroll, billing, technical support, cancellations, and privacy enquiries."
      />

      <section className="cu-hero">
        <div className="cu-hero-inner">
          <p className="au-eyebrow">Contact Us</p>
          <h1>We’re here to help</h1>
          <p>
            If you have questions about Brixigo HRMS, payroll, subscriptions, billing, technical support, or any of our services, please get in touch with our team.
          </p>
        </div>
      </section>

      <section className="cu-section" aria-labelledby="get-in-touch-heading">
        <ScrollReveal className="cu-section-head">
          <h2 id="get-in-touch-heading">Get in Touch</h2>
          <p>{COMPANY.legalName}</p>
        </ScrollReveal>
        <div className="cu-cards">
          <ContactCard icon={Mail} label="Email" value={COMPANY.email} href={`mailto:${COMPANY.email}`} />
          <ContactCard icon={Phone} label="Phone" value={COMPANY.phone} href={COMPANY.phoneHref} />
          <ContactCard icon={MapPin} label="Registered Office" value={COMPANY.officeLines.join(", ")} />
        </div>
        <address className="cu-office">
          <strong>Registered Office</strong>
          {COMPANY.officeLines.map((line) => (
            <span key={line}>{line}</span>
          ))}
        </address>
      </section>

      <section className="cu-section cu-section-alt" aria-labelledby="support-heading">
        <div className="cu-two-col">
          <ScrollReveal>
            <div className="cu-panel">
              <div className="cu-panel-icon" aria-hidden="true"><Headset size={22} /></div>
              <h2 id="support-heading">Customer Support</h2>
              <p>For assistance with our HRMS and payroll software, you can contact us regarding:</p>
              <ul className="cu-list">
                {SUPPORT_TOPICS.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <p>
                When contacting support regarding an account or payment, please include your registered email address and relevant account, invoice, or transaction details so that we can assist you more efficiently.
              </p>
            </div>
          </ScrollReveal>
          <ScrollReveal delay={80}>
            <div className="cu-panel">
              <div className="cu-panel-icon" aria-hidden="true"><Briefcase size={22} /></div>
              <h2>Business Enquiries</h2>
              <p>For product enquiries, partnerships, or business-related questions, please contact us at:</p>
              <p>
                Email: <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>
                <br />
                Phone: <a href={COMPANY.phoneHref}>{COMPANY.phone}</a>
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section className="cu-section" aria-labelledby="policy-heading">
        <div className="cu-policy-grid">
          <ScrollReveal className="cu-panel">
            <div className="cu-panel-icon" aria-hidden="true"><FileText size={22} /></div>
            <h2 id="policy-heading">Cancellation &amp; Refund Requests</h2>
            <p>
              For subscription cancellation or refund-related requests, please email:{" "}
              <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>
            </p>
            <p>
              Please refer to our{" "}
              <Link to={MARKETING_ROUTES.refund}>Cancellation &amp; Refund Policy</Link>{" "}
              for information about eligibility, cancellation terms, and refund processing.
            </p>
          </ScrollReveal>
          <ScrollReveal className="cu-panel" delay={60}>
            <div className="cu-panel-icon" aria-hidden="true"><Shield size={22} /></div>
            <h2>Privacy-Related Enquiries</h2>
            <p>
              For questions regarding the collection, use, or protection of your personal information, please contact us at:{" "}
              <a href={`mailto:${COMPANY.hrEmail}`}>{COMPANY.hrEmail}</a>
            </p>
            <p>
              Please refer to our{" "}
              <Link to={MARKETING_ROUTES.privacy}>Privacy Policy</Link>{" "}
              for more information about how Brixigo handles personal information.
            </p>
          </ScrollReveal>
          <ScrollReveal className="cu-panel" delay={120}>
            <div className="cu-panel-icon" aria-hidden="true"><FileText size={22} /></div>
            <h2>Terms of Use</h2>
            <p>
              For information regarding the use of the Brixigo Platform, customer responsibilities, and applicable terms, please refer to our{" "}
              <Link to={MARKETING_ROUTES.terms}>Terms of Use</Link>.
            </p>
          </ScrollReveal>
        </div>
      </section>

      <section className="cu-section cu-section-alt" aria-labelledby="form-heading">
        <div className="cu-form-wrap">
          <ScrollReveal className="cu-form-copy">
            <h2 id="form-heading">Contact Form</h2>
            <p>You may also contact us using the form provided on this page.</p>
          </ScrollReveal>
          <ScrollReveal delay={80}>
            <ContactForm />
          </ScrollReveal>
        </div>
      </section>

      <section className="cu-section" aria-labelledby="commitment-heading">
        <ScrollReveal className="cu-commit">
          <h2 id="commitment-heading">Our Commitment</h2>
          <p>
            Brixigo Technologies Private Limited is committed to providing reliable HRMS and payroll software and responsive customer support to businesses using our platform.
          </p>
          <p>
            We will make reasonable efforts to respond to customer enquiries and support requests as promptly as possible.
          </p>
        </ScrollReveal>
        <div className="cu-legal-foot">
          <strong>{COMPANY.legalName}</strong>
          <span>{COMPANY.address}</span>
          <span>
            Email: <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>
          </span>
          <span>
            Phone: <a href={COMPANY.phoneHref}>{COMPANY.phone}</a>
          </span>
        </div>
      </section>
    </MarketingLayout>
  );
}
